import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ContentIdea } from '@/types/dashboard';
import { Heart, ChevronLeft, ChevronRight, TrendingUp, Sparkles, MessageSquare, Layers, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CorkModernModal } from './CorkModernModal';
import { useFavorites } from '@/hooks/useFavorites';

interface CorkModernViewProps {
  ideas: ContentIdea[];
  onIdeaClick?: (idea: ContentIdea) => void;
  isTrashView?: boolean;
  onTrashAction?: (ideaId: string, source: 'scraped' | 'user_generated') => void;
  onFavoriteToggle?: () => void;
  onUsedToggle?: () => void;
}

const IDEAS_PER_PAGE = 9;

// Enhanced circular viral score indicator with glow
const ViralScoreRing = ({ score }: { score: number }) => {
  const normalizedScore = Math.max(0, Math.min(100, score));
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference - (normalizedScore / 100) * circumference;
  
  const getScoreColor = (s: number) => {
    if (s >= 70) return 'text-emerald-500';
    if (s >= 50) return 'text-amber-500';
    return 'text-slate-400';
  };

  const getScoreBg = (s: number) => {
    if (s >= 70) return 'stroke-emerald-500';
    if (s >= 50) return 'stroke-amber-500';
    return 'stroke-slate-400';
  };

  const getScoreGlow = (s: number) => {
    if (s >= 70) return 'shadow-emerald-500/30';
    if (s >= 50) return 'shadow-amber-500/30';
    return 'shadow-slate-400/20';
  };

  return (
    <motion.div 
      className={cn(
        "relative w-12 h-12 flex items-center justify-center rounded-full",
        getScoreGlow(normalizedScore)
      )}
      whileHover={{ scale: 1.05 }}
    >
      <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 40 40">
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="text-muted/20"
        />
        <motion.circle
          cx="20"
          cy="20"
          r="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={cn("transition-colors duration-700", getScoreBg(normalizedScore))}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-[10px] font-bold leading-none", getScoreColor(normalizedScore))}>
          {Math.round(normalizedScore)}
        </span>
      </div>
    </motion.div>
  );
};

const getScoreBorderColor = (score: number | null) => {
  const viralScore = score || 0;
  if (viralScore >= 70) return 'border-l-emerald-500';
  if (viralScore >= 50) return 'border-l-amber-500';
  return 'border-l-slate-400/50';
};

const getScoreGlowColor = (score: number | null) => {
  const viralScore = score || 0;
  if (viralScore >= 70) return 'hover:shadow-emerald-500/10 hover:border-emerald-500/30';
  if (viralScore >= 50) return 'hover:shadow-amber-500/10 hover:border-amber-500/30';
  return 'hover:shadow-slate-400/5 hover:border-slate-400/20';
};

const formatTimeAgo = (dateString: string) => {
  const now = new Date();
  const created = new Date(dateString);
  const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / 60000);
  
  if (diffInMinutes < 1) return 'Przed chwilą';
  if (diffInMinutes < 60) return `${diffInMinutes} min temu`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} godz. temu`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return '1 dzień temu';
  if (diffInDays < 7) return `${diffInDays} dni temu`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} tyg. temu`;
  return `${Math.floor(diffInDays / 30)} mies. temu`;
};

const getScriptContent = (content: string | null): string => {
  if (!content) return 'Brak treści scenariusza';
  const normalizeNewlines = (text: string): string => {
    return text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\\n\\n/g, '\n\n')
      .replace(/\\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === 'object' && parsed !== null) {
      const parsedObj = parsed as Record<string, unknown>;
      const script = parsedObj.script;
      const contentField = parsedObj.content;
      if (typeof script === 'string') return normalizeNewlines(script);
      if (typeof contentField === 'string') return normalizeNewlines(contentField);
    }
    return normalizeNewlines(content);
  } catch {
    return normalizeNewlines(content);
  }
};

// Enhanced Bento Grid Card Component
const BentoCard = ({
  idea,
  index,
  onClick,
  isFeatured,
  isFavoritedOverride,
  onFavoriteToggle,
}: {
  idea: ContentIdea;
  index: number;
  onClick: () => void;
  isFeatured?: boolean;
  isFavoritedOverride?: boolean;
  onFavoriteToggle: (idea: ContentIdea) => void;
}) => {
  const isFavorited = isFavoritedOverride ?? idea.is_favorited ?? false;
  const viralScore = idea.source === 'user_generated' ? null : idea.viral_score;
  const showScore = viralScore !== null && viralScore !== 0;
  const isHighScore = showScore && viralScore >= 70;
  
  // Determine badges based on available data
  const hasHooks = Array.isArray(idea.alt_hooks) && idea.alt_hooks.length > 0;
  const hasOverlays = Array.isArray(idea.text_overlays) && idea.text_overlays.length > 0;
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.06,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      whileHover={{ 
        y: -4,
        transition: { duration: 0.2 }
      }}
      onClick={onClick}
      className={cn(
        "group cursor-pointer relative overflow-hidden",
        "bg-gradient-to-br from-card/95 to-card/70",
        "backdrop-blur-xl",
        "border border-border/40",
        "rounded-2xl",
        "transition-all duration-300",
        "shadow-lg shadow-black/5",
        getScoreGlowColor(viralScore),
        isFeatured ? "md:col-span-2" : "",
        "border-l-4",
        getScoreBorderColor(viralScore),
        "hover:shadow-2xl"
      )}
    >
      {/* Animated gradient background on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none">
        <div className={cn(
          "absolute inset-0 bg-gradient-to-br",
          isHighScore 
            ? "from-emerald-500/5 via-transparent to-teal-500/5"
            : "from-primary/5 via-transparent to-secondary/5"
        )} />
      </div>
      
      {/* Top shimmer effect */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      
      {/* Card Content */}
      <div className={cn(
        "relative z-10",
        isFeatured ? "p-5 sm:p-6" : "p-4 sm:p-5"
      )}>
        {/* Top Row: Viral Score + Favorite + Indicators */}
        <div className="flex items-start justify-between gap-3 mb-3">
          {/* Viral Score or User Icon */}
          <div className="flex items-center gap-3">
            {showScore ? (
              <div className="flex items-center gap-2">
                <ViralScoreRing score={viralScore} />
                <div className="hidden sm:block">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                    Viral Score
                  </p>
                  <p className={cn(
                    "text-sm font-bold",
                    viralScore >= 70 ? "text-emerald-600 dark:text-emerald-400" :
                    viralScore >= 50 ? "text-amber-600 dark:text-amber-400" :
                    "text-slate-600 dark:text-slate-400"
                  )}>
                    {viralScore.toFixed(1)}
                    <span className="text-xs opacity-60 ml-0.5">/100</span>
                  </p>
                </div>
              </div>
            ) : idea.source === 'user_generated' ? (
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-secondary/10 flex items-center justify-center shadow-sm">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="hidden sm:block">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                    Twoje
                  </p>
                  <p className="text-sm font-bold text-primary">
                    Scenariusz
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-xl bg-muted/30 border border-border/30 flex items-center justify-center shadow-sm">
                  <TrendingUp className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <div className="hidden sm:block">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                    Viral Score
                  </p>
                  <p className="text-sm font-medium text-muted-foreground/60">
                    Trwa obliczanie
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right side: Indicators + Favorite */}
          <div className="flex items-center gap-2">
            {/* Content indicators */}
            <div className="hidden sm:flex items-center gap-1">
              {hasHooks && (
                <div className="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center" title="Ma hook">
                  <Zap className="h-3 w-3 text-blue-500" />
                </div>
              )}
              {hasOverlays && (
                <div className="w-6 h-6 rounded-md bg-orange-500/10 flex items-center justify-center" title="Ma nakładki">
                  <Layers className="h-3 w-3 text-orange-500" />
                </div>
              )}
            </div>

            {/* Favorite Icon */}
            <motion.div
              className={cn(
                "h-9 w-9 rounded-xl flex items-center justify-center transition-all duration-300",
                isFavorited
                  ? "text-red-500 bg-red-50 dark:bg-red-950/30 shadow-sm"
                  : "text-muted-foreground/30 group-hover:text-muted-foreground group-hover:bg-muted/50"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onFavoriteToggle(idea);
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Heart
                className={cn(
                  "h-4 w-4 transition-all duration-300",
                  isFavorited && "fill-red-500 scale-110"
                )}
              />
            </motion.div>
          </div>
        </div>

        {/* Title */}
        <h3 className={cn(
          "font-semibold leading-snug text-foreground mb-2 line-clamp-2",
          isFeatured ? "text-lg sm:text-xl" : "text-base sm:text-lg",
          "group-hover:text-primary transition-colors duration-300"
        )}>
          {idea.title}
        </h3>

        {/* Description Preview */}
        <p className={cn(
          "text-muted-foreground line-clamp-2 mb-4",
          isFeatured ? "text-sm" : "text-xs sm:text-sm"
        )}>
          {idea.description || idea.content || 'Brak opisu'}
        </p>

        {/* Bottom Row: Timestamp & Status */}
        <div className="flex items-center justify-between pt-3 border-t border-border/30">
          <span className="text-xs text-muted-foreground/70">
            {formatTimeAgo(idea.created_at)}
          </span>
          <div className="flex items-center gap-2">
            {idea.is_used && (
              <motion.span 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-medium"
              >
                Użyty
              </motion.span>
            )}
            {isHighScore && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500/15 to-teal-500/10 text-emerald-600 dark:text-emerald-400 font-medium shadow-sm"
              >
                <TrendingUp className="h-3 w-3" />
                <span className="hidden sm:inline">Viral</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const CorkModernView = ({
  ideas,
  isTrashView = false,
  onTrashAction,
  onFavoriteToggle: onFavoriteToggleParent,
  onUsedToggle: onUsedToggleParent,
}: CorkModernViewProps) => {
  const navigate = useNavigate();
  const { toggleFavorite } = useFavorites();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIdea, setSelectedIdea] = useState<ContentIdea | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Calculate ideas that fit in 9 grid slots (featured cards take 2 slots)
  const getIdeasForPage = (page: number) => {
    const slotsPerPage = 9;
    let slotCount = 0;
    let ideaIndex = 0;
    const pageIdeas: Array<{ idea: ContentIdea; isFeatured: boolean }> = [];
    
    // Skip ideas from previous pages
    let currentSlot = 0;
    let startIdeaIndex = 0;
    
    // Find start index based on previous pages
    while (currentSlot < (page - 1) * slotsPerPage && startIdeaIndex < ideas.length) {
      const idea = ideas[startIdeaIndex];
      const viralScore = idea.source === 'user_generated' ? null : idea.viral_score;
      const isHighScore = viralScore !== null && viralScore !== 0 && viralScore >= 70;
      const slotsNeeded = isHighScore ? 2 : 1;
      
      if (currentSlot + slotsNeeded > (page - 1) * slotsPerPage) {
        break;
      }
      currentSlot += slotsNeeded;
      startIdeaIndex++;
    }
    
    // Fill current page
    ideaIndex = startIdeaIndex;
    while (slotCount < slotsPerPage && ideaIndex < ideas.length) {
      const idea = ideas[ideaIndex];
      const viralScore = idea.source === 'user_generated' ? null : idea.viral_score;
      const isHighScore = viralScore !== null && viralScore !== 0 && viralScore >= 70;
      const slotsNeeded = isHighScore ? 2 : 1;
      
      if (slotCount + slotsNeeded > slotsPerPage) {
        break;
      }
      
      pageIdeas.push({ idea, isFeatured: isHighScore });
      slotCount += slotsNeeded;
      ideaIndex++;
    }
    
    return pageIdeas;
  };
  
  // Calculate total pages based on slot usage
  const getTotalPages = () => {
    const slotsPerPage = 9;
    let totalSlots = 0;
    
    ideas.forEach(idea => {
      const viralScore = idea.source === 'user_generated' ? null : idea.viral_score;
      const isHighScore = viralScore !== null && viralScore !== 0 && viralScore >= 70;
      totalSlots += isHighScore ? 2 : 1;
    });
    
    return Math.ceil(totalSlots / slotsPerPage);
  };
  
  const totalPages = getTotalPages();
  const currentIdeas = getIdeasForPage(currentPage);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleIdeaClick = (idea: ContentIdea) => {
    // Apply any optimistic overrides so the modal shows the correct state
    const favOverride = favoriteOverrides[idea.id];
    const usedOverride = usedOverrides[idea.id];
    const updatedIdea = {
      ...idea,
      ...(favOverride !== undefined && { is_favorited: favOverride }),
      ...(usedOverride !== undefined && { is_used: usedOverride }),
    };
    setSelectedIdea(updatedIdea);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedIdea(null);
  };

  const handleCopyScript = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  // Track optimistic favorite overrides: ideaId -> toggled state
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, boolean>>({});
  // Track optimistic used overrides: ideaId -> toggled state
  const [usedOverrides, setUsedOverrides] = useState<Record<string, boolean>>({});

  const handleCardFavoriteToggle = async (idea: ContentIdea) => {
    const currentStatus = favoriteOverrides[idea.id] ?? idea.is_favorited ?? false;
    const newStatus = !currentStatus;
    setFavoriteOverrides(prev => ({ ...prev, [idea.id]: newStatus }));
    const success = await toggleFavorite(idea.id, currentStatus, idea.source);
    if (!success) {
      setFavoriteOverrides(prev => ({ ...prev, [idea.id]: currentStatus }));
    } else {
      onFavoriteToggleParent?.();
    }
  };

  const handleModalFavoriteToggle = () => {
    // Sync the modal's toggled state back to card overrides
    if (selectedIdea) {
      const current = favoriteOverrides[selectedIdea.id] ?? selectedIdea.is_favorited ?? false;
      setFavoriteOverrides(prev => ({ ...prev, [selectedIdea.id]: !current }));
    }
    onFavoriteToggleParent?.();
  };

  const handleUsedToggle = () => {
    // Sync the modal's toggled state back to card overrides
    if (selectedIdea) {
      const current = usedOverrides[selectedIdea.id] ?? selectedIdea.is_used ?? false;
      setUsedOverrides(prev => ({ ...prev, [selectedIdea.id]: !current }));
    }
    onUsedToggleParent?.();
  };

  const handleTrash = (ideaId: string, source: 'scraped' | 'user_generated') => {
    handleModalClose();
    if (onTrashAction) {
      onTrashAction(ideaId, source);
    }
  };

  return (
    <div className="w-full">
      {/* Empty State */}
      <AnimatePresence mode="wait">
        {currentIdeas.length === 0 ? (
          <motion.div 
            key="empty"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="py-20 text-center"
          >
            <motion.div 
              className="w-28 h-28 mx-auto bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 rounded-3xl flex items-center justify-center mb-8 shadow-2xl shadow-primary/10"
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Sparkles className="h-12 w-12 text-primary/60" />
            </motion.div>
            <h3 className="text-2xl font-bold text-foreground mb-3">
              Brak pomysłów
            </h3>
            <p className="text-muted-foreground text-base max-w-md mx-auto mb-8 leading-relaxed">
              Przygotuj pierwszy scenariusz w Kreatorze, lub poczekaj na nowe pomysły.
            </p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                onClick={() => navigate('/kreator-scenariusza')}
                className="gap-2 px-8 py-6 text-base rounded-2xl shadow-lg shadow-primary/20 bg-gradient-to-r from-primary to-primary/90"
                size="lg"
              >
                <Sparkles className="h-5 w-5" />
                Kreator scenariuszy
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Bento Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 auto-rows-fr">
              <AnimatePresence mode="popLayout">
                {currentIdeas.map(({ idea, isFeatured }, index) => (
                  <BentoCard
                    key={idea.id}
                    idea={idea}
                    index={index}
                    onClick={() => handleIdeaClick(idea)}
                    isFeatured={isFeatured}
                    isFavoritedOverride={favoriteOverrides[idea.id]}
                    onFavoriteToggle={handleCardFavoriteToggle}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination Controls */}
            <AnimatePresence>
              {totalPages > 1 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-12 flex items-center justify-center gap-4"
                >
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="gap-2 h-11 px-5 rounded-xl border-border/50 hover:border-primary/30 hover:bg-primary/5"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Poprzednia</span>
                    </Button>
                  </motion.div>
                  
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <motion.button
                            key={page}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setCurrentPage(page)}
                            className={cn(
                              "w-11 h-11 rounded-xl font-semibold text-sm transition-all duration-200",
                              currentPage === page 
                                ? "bg-gradient-to-r from-primary to-primary/90 text-primary-foreground shadow-lg shadow-primary/25"
                                : "bg-background border border-border/50 hover:border-primary/30 hover:bg-primary/5 text-foreground"
                            )}
                          >
                            {page}
                          </motion.button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2 text-muted-foreground">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="gap-2 h-11 px-5 rounded-xl border-border/50 hover:border-primary/30 hover:bg-primary/5"
                    >
                      <span className="hidden sm:inline">Następna</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Results Summary */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-8 text-center"
            >
              <p className="text-sm text-muted-foreground">
                Pokazano{' '}
                <span className="font-semibold text-foreground bg-muted/50 px-2 py-0.5 rounded-md">
                  {currentIdeas.length}
                </span>{' '}
                z{' '}
                <span className="font-semibold text-foreground bg-muted/50 px-2 py-0.5 rounded-md">
                  {ideas.length}
                </span>{' '}
                pomysłów
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CorkModernModal Integration */}
      <CorkModernModal
        idea={selectedIdea}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onCopyScript={handleCopyScript}
        onFavoriteToggle={handleModalFavoriteToggle}
        onUsedToggle={handleUsedToggle}
        onTrash={isTrashView ? undefined : handleTrash}
      />
    </div>
  );
};

export default CorkModernView;
